<script setup lang="ts">
/**
 * MY — Incorporation DOC (M32014) Studio panel.
 * Live document: Section 14 + Section 201 (± attestation) — Director resignation 패턴.
 */
import { ref, reactive, computed, provide, watch, onMounted, nextTick } from 'vue';
import { useRouter } from 'vue-router';
import type { PreSetSelection } from '@/features/startTask/preset_task/shared/preset/types/preset.types';
import type { OfficerDetail } from '@/features/AI-documents/types/docGen.types';
import { getTaskConfigByCode } from '@/features/startTask/api/taskConfigApi';
import { CDD_DOC_CTX } from './cdd/cddContext';
import {
  labelize,
  typeOf,
  progressKeysForRows,
  DOC_SHEET_PATTERNS,
} from './incorporation/incorporationDocFields';
import IncorporationDoc from './incorporation/IncorporationDoc.vue';
import {
  buildIncorporationDocFormData,
  isForeignDirector,
} from '../mappers/incorporationDocMapper';
import { mapIncorporationToIncDocValues } from '../mappers/mapIncorporationToIncDocValues';
import { autoMapVariables } from '@/features/startTask/preset_task/my/incorporation/composables/incorporationVariableMapper';
import { parseMalaysianAddress, isEmptyMyAddress } from '@/shared/utils/malaysianAddress';
import { usePresetProgress } from '@/features/startTask/preset_task/shared/preset/composables/usePresetProgress';
import AddressInputModal from './cdd/AddressInputModal.vue';
import { comboSourceOf } from './cdd/cddFields';
import { comboOptions, resolveIdType, resolveRace } from './cdd/cddOptions';
import { entityMyApi } from '@/features/Entity_my/api/entityApi';
import { useSessionStore } from '@/shared/stores/sessionStore';

const props = defineProps<{
  selection: PreSetSelection;
  entityData: Record<string, any> | null;
  taskTitle: string;
  isReady: boolean;
  currentStep: string;
  selectedPerson?: OfficerDetail | null;
  activePersonContext?: Record<string, any> | null;
  selectedAddress?: { value: string; type: string; position?: string } | null;
  activeAddressContext?: { fieldGroup: string; index: number } | null;
  editTaskId?: number | null;
  studio?: boolean;
  pendingDocType?: string;
  appliedAddress?: {
    key: string;
    line: string;
    town: string;
    state: string;
    postcode: string;
    country: string;
    seq: number;
  } | null;
  chatFormData?: Record<string, any> | null;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'task-created', data: { taskId: number; taskTitle: string }): void;
  (e: 'request-person-lookup', ctx: Record<string, any>): void;
  (e: 'change-entity'): void;
  (e: 'change-task-title'): void;
  (e: 'progress-change', pct: number): void;
  (e: 'request-address-input', key: string): void;
}>();

const router = useRouter();
const docRef = ref<InstanceType<typeof IncorporationDoc> | null>(null);
const values = reactive<Record<string, string>>({});
const activeKey = ref('');
const activeCheckbox = ref('');
const inputKey = ref('');
const inputText = ref('');
const dateMenu = ref(false);

const entityCd = computed(() =>
  String(
    props.entityData?.entityCd ||
      props.entityData?.icpCd ||
      props.chatFormData?.entityCd ||
      props.chatFormData?.icpCd ||
      '',
  ).trim(),
);

const taskTitleLocal = ref('');
const titleModel = computed({
  get: () => taskTitleLocal.value || props.taskTitle || '',
  set: (v: string) => {
    taskTitleLocal.value = v;
  },
});
const effectiveTitle = computed(() => titleModel.value.trim());

function setFieldValue(key: string, val: string) {
  values[key] = val;
}
function activate(key: string) {
  activeKey.value = key;
  const t = typeOf(key);
  if (t === 'msic' || /^business_nature_\d+$/.test(key) || /^msic_code_\d+$/.test(key)) {
    const slot =
      key.match(/^(?:business_nature|msic_code)_(\d+)$/)?.[1] || '1';
    emit('request-person-lookup', {
      fieldGroup: 'incorporation-doc',
      key: `business_nature_${slot}`,
      lookupType: 'msic',
      msicSlot: Number(slot),
    });
    inputKey.value = '';
    return;
  }
  if (t === 'person' || t === 'entity') {
    const defaultRole = /^mem\d+_name$/.test(key)
      ? 'SHAREHOLDER'
      : t === 'person'
        ? 'DIRECTOR'
        : null;
    emit('request-person-lookup', {
      fieldGroup: 'incorporation-doc',
      key,
      defaultRole,
      // Proposed company name → incorporation 테이블 목록
      lookupType: key === 'company_name' || t === 'entity' ? 'incorporation' : 'officer',
    });
    inputKey.value = '';
  } else {
    inputKey.value = key;
    inputText.value = values[key] || '';
    if (t === 'date') dateMenu.value = true;
  }
}
function onPickDate(d: any) {
  const date = Array.isArray(d) ? d[0] : d;
  if (!date) return;
  const dt = new Date(date);
  inputText.value = Number.isNaN(dt.getTime())
    ? String(date)
    : `${String(dt.getDate()).padStart(2, '0')}/${String(dt.getMonth() + 1).padStart(2, '0')}/${dt.getFullYear()}`;
  dateMenu.value = false;
  commitInput();
}
function commitInput() {
  if (inputKey.value) values[inputKey.value] = inputText.value.trim();
  inputKey.value = '';
  activeKey.value = '';
}
function skipInput() {
  inputKey.value = '';
  activeKey.value = '';
}

const addrModal = ref(false);
const addrKey = ref('');
const addrLabel = ref('');
const addrInitial = ref<{
  line?: string;
  town?: string;
  state?: string;
  postcode?: string;
  country?: string;
}>({});

function openAddress(key: string) {
  if (props.studio) {
    addrKey.value = key;
    emit('request-address-input', key);
    return;
  }
  addrKey.value = key;
  addrLabel.value = labelize(key);
  addrInitial.value = { line: values[key] || '' };
  addrModal.value = true;
}
function applyAddressTo(
  key: string,
  a: { line: string; town: string; state: string; postcode: string; country: string },
) {
  if (!key) return;
  values[key] = [a.line, [a.postcode, a.town].filter(Boolean).join(' '), a.state, a.country]
    .map((s) => String(s || '').trim())
    .filter(Boolean)
    .join(', ')
    .toUpperCase();
}
function applyAddress(a: {
  line: string;
  town: string;
  state: string;
  postcode: string;
  country: string;
}) {
  applyAddressTo(addrKey.value, a);
}
watch(
  () => props.appliedAddress,
  (a) => {
    if (a?.key) applyAddressTo(a.key, a);
  },
);

function comboOptionsOf(key: string) {
  return comboOptions(comboSourceOf(key));
}
const pendingPickValue = ref('');
const stubRowCount = ref(0);

provide(CDD_DOC_CTX, {
  valueOf: (k) => values[k] || '',
  labelOf: labelize,
  typeOf,
  activeKey,
  activate,
  setFieldValue,
  openAddress,
  checkboxValue: activeCheckbox,
  setCheckbox: () => {},
  comboOptionsOf,
  shareholderRowCount: stubRowCount,
  boRowCount: stubRowCount,
  addShareholderRow: () => {},
  removeShareholderRow: () => {},
  addBoRow: () => {},
  removeBoRow: () => {},
  pendingPickValue,
  placePick: () => false,
});

function applyPerson(key: string, a: any) {
  const name =
    a.name ||
    a.contactName ||
    [a.firstName, a.middleName, a.surname].filter(Boolean).join(' ') ||
    '';
  values[key] = name;

  if (typeOf(key) === 'entity' || key === 'company_name') {
    values.company_name = name || a.entityName || a.companyName || values.company_name || '';
    const fullAddr = a.registeredAddress || a.primaryAddress || a.residentialAddress || '';
    const res =
      a.myResidential && !isEmptyMyAddress(a.myResidential)
        ? a.myResidential
        : parseMalaysianAddress(fullAddr);
    if (!isEmptyMyAddress(res)) {
      values.registered_address = [
        res.street || fullAddr,
        [res.postcode, res.town].filter(Boolean).join(' '),
        res.state,
        res.country,
      ]
        .filter(Boolean)
        .join(', ');
    } else if (fullAddr) {
      values.registered_address = fullAddr;
    }
    if (a.email || a.contactEmail) values.registered_email = a.email || a.contactEmail;
    return;
  }

  const dirM = key.match(/^dir(\d+)_name$/);
  if (dirM) {
    const id = Number(dirM[1]);
    const idNo = a.nricNo || a.passportNo || a.contactRegNo || '';
    if (idNo) values[`dir${id}_id_no`] = idNo;
    if (a.nricNo && !a.passportNo) values[`dir${id}_id_type`] = resolveIdType('NRIC') || 'NRIC';
    else if (a.passportNo) values[`dir${id}_id_type`] = resolveIdType('Passport') || 'Passport';
    else if (a.idType || a.idTypeCd) {
      const t = resolveIdType(a.idType || a.idTypeCd);
      if (t) values[`dir${id}_id_type`] = t;
    }
    if (a.nationality || a.nationalityName) {
      values[`dir${id}_nationality`] = a.nationality || a.nationalityName;
    }
    if (a.dateOfBirth || a.dob) values[`dir${id}_dob`] = a.dateOfBirth || a.dob;
    if (a.email || a.contactEmail) values[`dir${id}_email`] = a.email || a.contactEmail;
    if (a.race) {
      const r = resolveRace(a.race);
      if (r) values[`dir${id}_race`] = r;
    }
    const fullAddr = a.residentialAddress || a.primaryAddress || '';
    const res =
      a.myResidential && !isEmptyMyAddress(a.myResidential)
        ? a.myResidential
        : parseMalaysianAddress(fullAddr);
    if (!isEmptyMyAddress(res)) {
      values[`dir${id}_address`] = [
        res.street || fullAddr,
        [res.postcode, res.town].filter(Boolean).join(' '),
        res.state,
        res.country,
      ]
        .filter(Boolean)
        .join(', ');
    } else if (fullAddr) {
      values[`dir${id}_address`] = fullAddr;
    }
    return;
  }

  const memM = key.match(/^mem(\d+)_name$/);
  if (memM) {
    const id = Number(memM[1]);
    const idNo = a.nricNo || a.passportNo || a.contactRegNo || '';
    if (idNo) values[`mem${id}_id_no`] = idNo;
    if (a.nricNo && !a.passportNo) values[`mem${id}_id_type`] = resolveIdType('NRIC') || 'NRIC';
    else if (a.passportNo) values[`mem${id}_id_type`] = resolveIdType('Passport') || 'Passport';
    else if (a.idType || a.idTypeCd) {
      const t = resolveIdType(a.idType || a.idTypeCd);
      if (t) values[`mem${id}_id_type`] = t;
    }
    if (a.nationality || a.nationalityName) {
      values[`mem${id}_nationality`] = a.nationality || a.nationalityName;
    }
    if (a.email || a.contactEmail) values[`mem${id}_email`] = a.email || a.contactEmail;
    if (a.race) {
      const r = resolveRace(a.race);
      if (r) values[`mem${id}_race`] = r;
    }
    const fullAddr = a.residentialAddress || a.primaryAddress || '';
    if (fullAddr) values[`mem${id}_address`] = fullAddr;
    return;
  }

  if (key === 'signer_name') {
    values.signer_name = name;
    const idNo = a.nricNo || a.passportNo || a.contactRegNo || '';
    if (idNo) values.signer_nric_passport_no = idNo;
  }
}

watch(
  () => props.selectedPerson,
  (p) => {
    if (!p) return;
    const ctx = props.activePersonContext;
    if (!ctx?.key) return;
    applyPerson(String(ctx.key || ''), p as any);
  },
);

watch(
  () => props.chatFormData,
  (fd) => {
    if (!fd) return;
    if (fd.taskTitle || fd._taskTitle) {
      titleModel.value = String(fd.taskTitle || fd._taskTitle);
    }
    // 회사가 링크되어 있을 때만 company_name 반영 — Change→Skip 후 이전 회사명이 다시 채워지는 것 방지
    if (fd.company_name && entityCd.value) {
      values.company_name = String(fd.company_name);
    }
    if (fd.purpose) values.purpose = String(fd.purpose);
    if (fd.company_type) values.company_type = String(fd.company_type);
    if (fd.company_sub_type) values.company_sub_type = String(fd.company_sub_type);
    for (let n = 1; n <= 3; n++) {
      const nature = fd[`business_nature_${n}`];
      const code = fd[`msic_code_${n}`];
      if (nature) values[`business_nature_${n}`] = String(nature);
      if (code) values[`msic_code_${n}`] = String(code);
    }
  },
  { deep: true, immediate: true },
);

watch(
  values,
  () => {
    nextTick(() => docRef.value?.relayoutPages?.());
  },
  { deep: true },
);

/** T-Link / incorporation 선택 → 상세 조회 후 live DOC 자동 채움 */
const lastAutofillIcp = ref('');
/** 자동 채움으로 기록한 키 (진단/추적용) */
const autofilledKeys = ref<Set<string>>(new Set());

/**
 * 회사 링크 해제(Change → Skip / 선택 취소) 시 문서를 빈 상태로 되돌린다.
 * 자동채움뿐 아니라 chatFormData(formData) 경유로 채워진 값도 있어 values 전체를 비운다.
 */
function resetAutofilledValues() {
  for (const k of Object.keys(values)) {
    delete values[k];
  }
  autofilledKeys.value = new Set();
  lastAutofillIcp.value = '';
  void nextTick(() => {
    docRef.value?.restoreLocalState?.({ directorRows: [1], memberRows: [1] });
    docRef.value?.relayoutPages?.();
    emitProgress();
  });
}

async function resolveActivityNameByCode(incData: Record<string, any>): Promise<Map<string, string>> {
  const codes = new Set<string>();
  for (const raw of [
    incData.primaryActivity,
    incData.secondaryActivity,
    incData.tertiaryActivity,
    incData.msic_1,
    incData.msic_2,
    incData.msic_3,
    incData.primaryActivityCode,
    incData.secondaryActivityCode,
  ]) {
    const s = String(raw || '').trim();
    if (/^\d{4,5}$/.test(s)) codes.add(s);
  }
  if (!codes.size) return new Map();

  try {
    const sessionStore = useSessionStore();
    const jurisdiction = String(sessionStore.user?.customer?.jurisdiction || '').toUpperCase();
    let codeList: Array<{ code: string; name: string }> = [];

    if (jurisdiction === 'MY' || jurisdiction === 'MALAYSIA' || !jurisdiction) {
      const res = (await entityMyApi.getMalaysiaBusinessCodes({ crsFilableOnly: true })) as any;
      const raw = res?.data?.data || res?.data || [];
      codeList = Array.isArray(raw)
        ? raw.map((c: any) => ({
            code: String(c.code || c.TOTAL_CODE || c.msicCode || '').trim(),
            name: String(c.name || c.CODE_NAME || c.description || '').trim(),
          }))
        : [];
    } else {
      const res = (await entityMyApi.getSSICList()) as any;
      const codesArr = res?.data?.codes || res?.data?.data?.codes || [];
      codeList = Array.isArray(codesArr)
        ? codesArr.map((c: any) => ({
            code: String(c.code || c.TOTAL_CODE || '').trim(),
            name: String(c.name || c.CODE_NAME || '').trim(),
          }))
        : [];
    }

    const map = new Map<string, string>();
    for (const item of codeList) {
      if (item.code && codes.has(item.code) && item.name) map.set(item.code, item.name);
    }
    return map;
  } catch (err) {
    console.warn('[IncorporationDoc] MSIC name resolve failed:', err);
    return new Map();
  }
}

async function autofillFromIncorporation(icpCd: string) {
  const cd = String(icpCd || '').trim();
  if (!cd || cd === lastAutofillIcp.value) return;
  lastAutofillIcp.value = cd;
  try {
    const res = await fetch(`/api/aiDocuments/incorporation/entity-data/${cd}`, {
      credentials: 'include',
    });
    const json = await res.json();
    if (!json?.success) return;
    const incData = json.data?.incorporationData || {};
    // entity-data 요약 directors/shareholders 를 원본에 보강 (이름·국적 등)
    if ((!incData.directors || !incData.directors.length) && json.data?.directors?.length) {
      incData.directors = json.data.directors;
    }
    if ((!incData.shareholders || !incData.shareholders.length) && json.data?.shareholders?.length) {
      incData.shareholders = json.data.shareholders;
    }
    if (!incData.companyName && json.data?.companyName) {
      incData.companyName = json.data.companyName;
    }
    const activityNameByCode = await resolveActivityNameByCode(incData);
    const mapped = mapIncorporationToIncDocValues(incData, { activityNameByCode });
    const written = new Set<string>();
    for (const [k, v] of Object.entries(mapped.values)) {
      if (v) {
        values[k] = v;
        written.add(k);
      }
    }
    autofilledKeys.value = written;
    await nextTick();
    docRef.value?.restoreLocalState?.({
      directorRows: mapped.directorRows,
      memberRows: mapped.memberRows,
    });
    await nextTick();
    docRef.value?.relayoutPages?.();
    emitProgress();
  } catch (err) {
    console.warn('[IncorporationDoc] autofill failed:', err);
    lastAutofillIcp.value = '';
  }
}

watch(
  entityCd,
  (cd, prev) => {
    if (cd) {
      void autofillFromIncorporation(cd);
    } else if (prev) {
      // Change 로 회사 해제(선택 취소/Skip) → 자동 채운 값 초기화
      resetAutofilledValues();
    }
  },
  { immediate: true },
);

const studio = computed(() => !!props.studio);
const creating = ref(false);
const progress = ref('');
const presetProgress = usePresetProgress();
watch(creating, (v) => {
  presetProgress.busy = v;
  presetProgress.message = v ? progress.value || 'Creating task…' : '';
});
watch(progress, (m) => {
  if (creating.value) presetProgress.message = m;
});

const editMode = computed(() => !!props.editTaskId);
const editBaseline = ref<string | null>(null);
const taskConfigSteps = ref<string[]>([]);

onMounted(async () => {
  try {
    const cfg = await getTaskConfigByCode(String(props.selection.task.TASK_CODE || '').trim());
    // L_TASK_CONFIG bit → API processSteps: ['Doc', 'T-Sign', 'e-Filing']
    taskConfigSteps.value = Array.isArray(cfg?.processSteps)
      ? cfg.processSteps
      : Array.isArray((cfg as any)?.steps)
        ? (cfg as any).steps
        : [];
  } catch {
    /* ignore */
  }
  await nextTick();
  if (editMode.value) {
    try {
      editBaseline.value = JSON.stringify(gatherState());
    } catch {
      editBaseline.value = null;
    }
  }
});

function emitProgress() {
  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  const dirIds: number[] =
    Array.isArray(local.directorRows) && local.directorRows.length ? local.directorRows : [1];
  const memIds: number[] =
    Array.isArray(local.memberRows) && local.memberRows.length ? local.memberRows : [1];
  const keys = progressKeysForRows(dirIds, memIds);
  const filled = keys.filter((k) => !!String(values[k] || '').trim()).length;
  const pct = keys.length ? Math.round((filled / keys.length) * 100) : 0;
  emit('progress-change', pct);
}
watch(values, () => emitProgress(), { deep: true });
watch(
  () => (docRef.value as any)?.directorRows,
  () => emitProgress(),
  { deep: true },
);

const availableTemplates = computed(() =>
  (props.selection?.documents || []).filter((d) => d.tmpId),
);

function classifyDoc(name: string): 'section14' | 'section201Attestation' | 'section201' | 'other' {
  const n = String(name || '');
  if (DOC_SHEET_PATTERNS.section14.test(n)) return 'section14';
  if (DOC_SHEET_PATTERNS.section201Attestation.test(n)) return 'section201Attestation';
  if (DOC_SHEET_PATTERNS.section201.test(n)) return 'section201';
  return 'other';
}

/** Section 201 — 템플릿 실제 varKey 에 autoMap + live DOC 별칭 병합 */
async function buildSection201FormData(opts: {
  templateId: number;
  directorIndex: number;
  focusDirectorId: number;
  valuesSnap: Record<string, string>;
  directorIds: number[];
  memberIds: number[];
  incorporationData: Record<string, any>;
  tmplVarsById: Map<number, Array<{ varKey: string; varLabel: string }>>;
  signersForDoc: any[];
}): Promise<Record<string, any>> {
  const {
    templateId,
    directorIndex,
    focusDirectorId,
    valuesSnap,
    directorIds,
    memberIds,
    incorporationData,
    tmplVarsById,
    signersForDoc,
  } = opts;

  const aliases = buildIncorporationDocFormData({
    values: valuesSnap,
    directorIds,
    memberIds,
    focusDirectorId,
    signersForDoc,
  });

  const schema = tmplVarsById.get(templateId) || [];
  if (!schema.length) return aliases;

  const qaDirectors = directorIds.map((id) => ({
    name: String(valuesSnap[`dir${id}_name`] || '').trim(),
    idNo: String(valuesSnap[`dir${id}_id_no`] || '').trim(),
    idType: String(valuesSnap[`dir${id}_id_type`] || '').trim(),
    nationality: String(valuesSnap[`dir${id}_nationality`] || '').trim(),
    email: String(valuesSnap[`dir${id}_email`] || '').trim(),
    isForeign: isForeignDirector(valuesSnap, id),
    contactCd: '',
  }));
  const qa = {
    companyName: String(valuesSnap.company_name || '').trim(),
    directors: qaDirectors,
    shareholders: memberIds.map((id) => ({
      name: String(valuesSnap[`mem${id}_name`] || '').trim(),
      email: String(valuesSnap[`mem${id}_email`] || '').trim(),
      contactCd: '',
    })),
  } as any;

  // live DOC id 가 있으면 raw director 에도 주입 — autoMap 이 details/idNo 양쪽에서 찾도록
  const rawDirs = Array.isArray(incorporationData?.directors)
    ? incorporationData.directors.map((d: any) => ({ ...d }))
    : [];
  for (let i = 0; i < qaDirectors.length; i++) {
    const live = qaDirectors[i];
    if (!live?.idNo) continue;
    if (!rawDirs[i]) rawDirs[i] = { name: live.name };
    const row = rawDirs[i];
    if (!row.idNo) row.idNo = live.idNo;
    if (!row.identificationNo) row.identificationNo = live.idNo;
    const md = row.myDirectorDetails || row.myDetails || {};
    if (!md.idNumber) {
      row.myDirectorDetails = { ...md, idNumber: live.idNo, idType: live.idType || md.idType };
    }
  }
  const incForMap = { ...incorporationData, directors: rawDirs.length ? rawDirs : incorporationData?.directors };

  const mapped = autoMapVariables(schema, incForMap, qa, 'director', directorIndex);
  // live DOC 날짜/회사명 우선, autoMap 이 채운 템플릿 키 우선, 별칭은 빈 키 보충
  const merged: Record<string, any> = { ...aliases, ...mapped };
  if (valuesSnap.declaration_date) {
    merged.declaration_date = valuesSnap.declaration_date;
    merged.date_of_declaration = valuesSnap.declaration_date;
  }
  if (valuesSnap.company_name) {
    merged.company_name = valuesSnap.company_name;
    merged.proposed_company_name = valuesSnap.company_name;
  }
  // autoMap 이 ID 키를 못 채웠으면 별칭에서 보충
  const idFromLive = String(valuesSnap[`dir${focusDirectorId}_id_no`] || '').trim();
  if (idFromLive) {
    for (const v of schema) {
      const key = String(v.varKey || '').trim();
      if (!key) continue;
      const n = key.toLowerCase().replace(/[\s_-]+/g, '');
      const looksLikeId =
        /nric|passport|icpassport|idno|identification|contactid/.test(n) &&
        !/expiry|expire|type|date/.test(n);
      if (looksLikeId && !String(merged[key] || '').trim()) {
        merged[key] = idFromLive;
      }
    }
  }
  return merged;
}

function gatherState() {
  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  return { values: { ...values }, local };
}
function restoreState(s: any) {
  if (s?.values) Object.assign(values, s.values);
  if (s?.local) (docRef.value as any)?.restoreLocalState?.(s.local);
}

async function handleCreateTask() {
  if (creating.value) return;
  if (!effectiveTitle.value) {
    progress.value = 'Please enter a Task Title.';
    return;
  }
  if (inputKey.value) commitInput();

  const local = (docRef.value as any)?.gatherLocalState?.() || {};
  const directorIds: number[] =
    Array.isArray(local.directorRows) && local.directorRows.length ? local.directorRows : [1];
  const memberIds: number[] =
    Array.isArray(local.memberRows) && local.memberRows.length ? local.memberRows : [1];
  const valuesSnap = { ...values };

  if (!String(valuesSnap.company_name || '').trim()) {
    progress.value = '⚠ Please fill Company Name.';
    return;
  }
  if (!directorIds.some((id) => !!String(valuesSnap[`dir${id}_name`] || '').trim())) {
    progress.value = '⚠ Please fill at least one Director Name.';
    return;
  }

  const docs = availableTemplates.value;
  if (!docs.length) {
    progress.value = '⚠ No templates linked to this preset — cannot generate documents.';
    return;
  }

  creating.value = true;
  progress.value = editMode.value ? 'Updating task…' : 'Creating task…';
  try {
    const steps = (taskConfigSteps.value || []).map((s) => String(s).toLowerCase());
    const taskFlow = {
      createDocument: steps.length === 0 ? true : steps.includes('doc'),
      requestTSign: steps.includes('t-sign'),
      proceedWithEFiling: steps.includes('e-filing'),
    };
    const officerData = {
      entityCd: entityCd.value || null,
      presetForm: gatherState(),
      directors: directorIds.map((id) => ({
        name: valuesSnap[`dir${id}_name`],
        idNo: valuesSnap[`dir${id}_id_no`],
        nationality: valuesSnap[`dir${id}_nationality`],
        isForeign: isForeignDirector(valuesSnap, id),
      })),
      members: memberIds.map((id) => ({
        name: valuesSnap[`mem${id}_name`],
        idNo: valuesSnap[`mem${id}_id_no`],
        shares: valuesSnap[`mem${id}_no_of_shares`],
      })),
    };

    let taskId: number | null = null;
    if (editMode.value) {
      taskId = Number(props.editTaskId) || null;
      if (!taskId) {
        progress.value = 'Error: invalid edit task id';
        creating.value = false;
        return;
      }
      await fetch(`/api/startTask/tasks/${taskId}/data`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskData: { formData: officerData } }),
      });
      await fetch(`/api/startTask/tasks/${taskId}/title`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ taskTitle: effectiveTitle.value }),
      });
    } else {
      const taskRes = await fetch('/api/startTask/tasks', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          entityCd: entityCd.value || null,
          taskTypeCd: props.selection.task.TASK_CODE,
          taskTypeName: props.selection.task.TASK_NAME,
          taskTitle: effectiveTitle.value,
          taskFlow,
          officerData,
        }),
      }).then((r) => r.json());
      taskId = taskRes?.data?.taskId || taskRes?.taskId;
      if (!taskId) {
        progress.value = `Error creating task: ${taskRes?.error?.message || taskRes?.message || 'no taskId'}`;
        creating.value = false;
        return;
      }
    }

    progress.value = 'Preparing documents…';

    type DocSave = {
      templateId: number;
      label: string;
      personName: string;
      entityCd?: string;
      formData: Record<string, any>;
      signers?: any[];
    };
    const docsToSave: DocSave[] = [];

    const section14 = docs.find((d) => classifyDoc(d.templateName || d.docCode || '') === 'section14');
    const s201 = docs.find((d) => classifyDoc(d.templateName || d.docCode || '') === 'section201');
    const s201a = docs.find(
      (d) => classifyDoc(d.templateName || d.docCode || '') === 'section201Attestation',
    );
    // fallback: unclassified templates by order
    const others = docs.filter(
      (d) => classifyDoc(d.templateName || d.docCode || '') === 'other',
    );

    // Section 201 — 템플릿 실제 placeholder + incorporation 원본으로 autoMap
    const tmplVarsById = new Map<number, Array<{ varKey: string; varLabel: string }>>();
    let incorporationData: Record<string, any> = {};
    try {
      const [tmplJson, entJson] = await Promise.all([
        fetch('/api/aiDocuments/incorporation/templates-with-variables', {
          credentials: 'include',
        }).then((r) => r.json()),
        entityCd.value
          ? fetch(`/api/aiDocuments/incorporation/entity-data/${entityCd.value}`, {
              credentials: 'include',
            }).then((r) => r.json())
          : Promise.resolve(null),
      ]);
      for (const t of tmplJson?.templates || []) {
        const tid = Number(t.templateId || t.tmpId || 0);
        if (!tid) continue;
        tmplVarsById.set(
          tid,
          (t.variables || []).map((v: any) => ({
            varKey: v.varKey || v,
            varLabel: v.varLabel || v.varKey || v,
          })),
        );
      }
      incorporationData = entJson?.data?.incorporationData || {};
    } catch (err) {
      console.warn('[IncorporationDOC] template/entity preload failed:', err);
    }

    if (section14?.tmpId) {
      docsToSave.push({
        templateId: section14.tmpId,
        label: String(section14.templateName || 'Section 14').replace(/\.docx?$/i, ''),
        personName: valuesSnap.company_name || 'Company',
        entityCd: entityCd.value || undefined,
        formData: buildIncorporationDocFormData({
          values: valuesSnap,
          directorIds,
          memberIds,
        }),
      });
    }

    for (let di = 0; di < directorIds.length; di++) {
      const id = directorIds[di];
      const name = valuesSnap[`dir${id}_name`] || `Director ${id}`;
      const foreign = isForeignDirector(valuesSnap, id);
      // 내국인 → Declaration(TMP001), 외국인 → Attestation(TMP002) — 교차 fallback 없음
      const tmpl = foreign ? s201a : s201;
      if (!tmpl?.tmpId) continue;
      const typeLabel = foreign ? 'With Attestation' : 'Declaration';
      const signersForDoc = [
        {
          id: `dir-${id}`,
          name,
          title: 'Director',
          role: 'director',
          email: valuesSnap[`dir${id}_email`] || '',
        },
      ];
      const formData = await buildSection201FormData({
        templateId: tmpl.tmpId,
        directorIndex: di,
        focusDirectorId: id,
        valuesSnap,
        directorIds,
        memberIds,
        incorporationData,
        tmplVarsById,
        signersForDoc,
      });
      docsToSave.push({
        templateId: tmpl.tmpId,
        label: `Section 201 (${typeLabel}) — ${name}`,
        personName: name,
        entityCd: entityCd.value || undefined,
        formData,
        signers: signersForDoc,
      });
    }

    // If no classified templates, dump all once
    if (!docsToSave.length && others.length) {
      for (const d of others) {
        if (!d.tmpId) continue;
        docsToSave.push({
          templateId: d.tmpId,
          label: String(d.templateName || 'Document').replace(/\.docx?$/i, ''),
          personName: valuesSnap.company_name || 'Company',
          entityCd: entityCd.value || undefined,
          formData: buildIncorporationDocFormData({
            values: valuesSnap,
            directorIds,
            memberIds,
          }),
        });
      }
    }

    if (docsToSave.length) {
      try {
        const saveRes = await fetch('/api/aiDocuments/incorporation/prepare-documents', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            taskId,
            documents: docsToSave,
            replaceExisting: editMode.value,
          }),
        }).then((r) => r.json());
        if (!saveRes?.success) {
          progress.value = `⚠ Documents: ${saveRes?.message || 'unknown'}`;
          console.warn('[IncorporationDOC] prepare-documents failed:', saveRes);
        }
      } catch (err: any) {
        console.warn('[IncorporationDOC] document save failed:', err);
        progress.value = `⚠ Document save failed: ${err?.message || err}`;
      }
    } else {
      progress.value = '⚠ No documents to save.';
    }

    try {
      await fetch(`/api/startTask/tasks/${taskId}/document-status`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch {
      /* ignore */
    }
    try {
      await fetch('/api/startTask/preset/use-log', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          presetTaskId: props.selection.task.PRESET_TASK_ID,
          taskId,
        }),
      });
    } catch {
      /* ignore */
    }
    try {
      progress.value = 'Loading next screen…';
      const [taskDetailRes, docListRes] = await Promise.all([
        fetch(`/api/startTask/tasks/${taskId}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
        fetch(`/api/startTask/doc-cust-save/${taskId}`, { credentials: 'include' })
          .then((r) => r.json())
          .catch(() => null),
      ]);
      sessionStorage.setItem(
        `presetTaskDetailPrefetch:${taskId}`,
        JSON.stringify({ taskDetail: taskDetailRes, docList: docListRes }),
      );
    } catch {
      /* ignore */
    }

    emit('task-created', { taskId: taskId!, taskTitle: effectiveTitle.value });
    router.push({ name: 'PreSetTaskDetail', query: { taskId: String(taskId) } });
  } catch (e: any) {
    console.error('[IncorporationDOC] create failed', e);
    progress.value = `Error: ${e?.message || e}`;
  } finally {
    creating.value = false;
  }
}

defineExpose({ gatherState, restoreState, createTask: handleCreateTask });
</script>

<template>
  <div class="rsn-panel" :class="{ 'rsn-studio': studio }">
    <div v-if="!studio" class="co-header">
      <div>
        <div class="co-title">{{ selection.task.TASK_NAME }}</div>
        <div class="co-subtitle">Incorporation DOC — Section 14 / Section 201</div>
      </div>
      <v-btn variant="text" size="small" class="text-none" @click="emit('back')">
        <v-icon start size="16">mdi-arrow-left</v-icon>Back
      </v-btn>
    </div>

    <div v-if="!studio" class="co-title-row">
      <span class="ct-label">Task Title</span>
      <v-text-field
        v-model="titleModel"
        placeholder="e.g. Incorporation (Pre) — NewCo Ventures"
        variant="outlined"
        density="compact"
        hide-details
        class="ct-input"
      />
    </div>

    <div v-if="!studio" class="co-entity">
      <v-icon size="18" color="primary">mdi-domain</v-icon>
      <span>{{ entityData?.companyName || entityData?.entityName || 'Entity (optional)' }}</span>
      <v-btn variant="tonal" size="x-small" class="text-none ml-auto" @click="emit('change-entity')">
        Change
      </v-btn>
    </div>

    <div v-if="inputKey" class="co-inputbar">
      <div class="lbl">
        {{ labelize(inputKey) }}
        <span v-if="typeOf(inputKey) === 'date'" class="opt">(optional)</span>
      </div>
      <div class="row">
        <v-menu v-if="typeOf(inputKey) === 'date'" v-model="dateMenu" :close-on-content-click="false">
          <template #activator="{ props: mp }">
            <v-text-field
              v-bind="mp"
              v-model="inputText"
              placeholder="DD/MM/YYYY"
              variant="outlined"
              density="compact"
              hide-details
              append-inner-icon="mdi-calendar"
            />
          </template>
          <v-date-picker @update:model-value="onPickDate" />
        </v-menu>
        <v-text-field
          v-else
          v-model="inputText"
          :placeholder="labelize(inputKey)"
          variant="outlined"
          density="compact"
          hide-details
          autofocus
          @keydown.enter="commitInput"
        />
        <v-btn color="primary" size="small" variant="flat" @click="commitInput">Set</v-btn>
        <v-btn v-if="typeOf(inputKey) === 'date'" variant="text" size="small" @click="skipInput">
          Skip
        </v-btn>
      </div>
    </div>

    <div class="co-doc-body">
      <IncorporationDoc ref="docRef" />
    </div>

    <div v-if="!studio || progress" class="co-footer">
      <v-btn v-if="!studio" variant="outlined" class="text-none" @click="emit('back')">Back</v-btn>
      <div class="co-foot-right">
        <span v-if="progress" class="co-progress" :class="{ err: /error|⚠/i.test(progress) }">
          {{ progress }}
        </span>
        <v-btn
          v-if="!studio"
          color="primary"
          class="text-none"
          :loading="creating"
          :disabled="creating"
          :prepend-icon="editMode ? 'mdi-content-save' : 'mdi-check'"
          @click="handleCreateTask"
        >
          {{ editMode ? 'Update Task' : 'Create Task' }}
        </v-btn>
      </div>
    </div>

    <AddressInputModal
      v-model="addrModal"
      :label="addrLabel"
      :has-components="false"
      :initial="addrInitial"
      @apply="applyAddress"
    />
  </div>
</template>

<style scoped>
.rsn-panel { display:flex; flex-direction:column; }
.rsn-studio { flex:0 0 auto; width:min(1000px, 100%); background:transparent; }
.rsn-studio .co-doc-body { background:transparent; padding:0; }
.co-header { display:flex; align-items:center; justify-content:space-between; padding:12px 16px; border-bottom:1px solid #eef0f6; }
.co-title { font-weight:700; font-size:15px; }
.co-subtitle { font-size:12px; color:#8a94a6; }
.co-title-row { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid #eef0f6; }
.co-title-row .ct-label { font-size:12px; font-weight:700; color:#5b6472; white-space:nowrap; }
.co-title-row .ct-input { max-width:520px; }
.co-entity { display:flex; align-items:center; gap:8px; background:#f0f5ff; padding:8px 14px; font-size:13px; }
.co-inputbar { padding:10px 14px; background:#fafbfe; border-bottom:1px solid #eef0f6; }
.co-inputbar .lbl { font-size:12px; font-weight:700; margin-bottom:6px; }
.co-inputbar .opt { font-weight:500; color:#8a94a6; }
.co-inputbar .row { display:flex; gap:8px; align-items:center; }
.co-doc-body { padding:16px 8px 24px; }
.co-footer { display:flex; justify-content:space-between; align-items:center; padding:12px 16px; border-top:1px solid #eef0f6; }
.co-foot-right { display:flex; align-items:center; gap:12px; }
.co-progress { font-size:12px; color:#5b6472; max-width:420px; }
.co-progress.err { color:#d64545; font-weight:600; }
</style>

<style>
/* secbar↔표만 붙이고, 블록 사이(gap 이후)는 분리 */
.cdd-tbl { width:100%; border-collapse:collapse; margin-top:0; }
.rsn-secbar + .cdd-tbl { margin-top:-1px; }
.cdd-tbl td, .cdd-tbl th { border:1px solid #000; padding:5px 8px; vertical-align:top; text-align:left; font-weight:400; }
.cdd-lcell { width:38%; background:#f7f8fb; font-weight:700 !important; font-size:12px; }
</style>
